import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useProductName, useVariantDescription } from '../../../../components/CartItemList/CartItemList';
import { giftPlanRowLabel } from '../../../../components/HardwareFirstGift/giftTermLabelKeys';
import { useGiftedTrackerPlan } from '../../../../components/HardwareFirstGift/useHardwareFirstGiftPricing';
import { Image } from '../../../../components/Image';
import { useCartPricing } from '../../../../contexts/CartPricingContext';
import { useLocale } from '../../../../contexts/LocaleContext';
import useProductSkuMap from '../../../../hooks/useProductSkuMap';
import CheckoutContext from '../../../../lib/CheckoutContext';
import { getFreeTrialCartItemIds } from '../../../../lib/cart';
import * as types from '../../../../types';
import { formatHardwarePrice, hardwareFirstCartImageUrl } from '../../../../lib/hardwareFirst';
import { checkoutImageUrlForProduct, checkoutImageUrlForProductWithVariant } from '../../../../lib/product';
import { getLocalizedPricing } from '../../../../lib/util/money';
import styles from './HardwareFirstCheckoutItems.module.scss';

/**
 * The order's lines as the checkout restates them: thumb, name, band, and the amount. A tracker
 * reads its list price less the code; anything else in the bag reads its list price. A line the catalog
 * cannot name is skipped, as the cart's own list does, rather than taking the page down.
 */
export default function HardwareFirstCheckoutItems() {
  const { t } = useTranslation();
  const { cart } = useContext(CheckoutContext);
  const { hardwareDiscounts } = useCartPricing();
  const locale = useLocale();
  const productSkuMap = useProductSkuMap();
  const productName = useProductName();
  const variantDescription = useVariantDescription();
  const products = useSelector((state: types.AppState) => state.config.products);
  const trialItemIds = getFreeTrialCartItemIds(cart, products);
  const giftedTrackerPlan = useGiftedTrackerPlan();

  return (
    <ul className={styles.list}>
      {Object.values(cart.cartItems).map((cartItem) => {
        const { product, variant } = productSkuMap.get(cartItem.lineItem.sku) ?? {};
        if (!product) {
          return null;
        }
        let name: string;
        let band: string | undefined;
        try {
          name = productName(cartItem);
          band = variantDescription(cartItem);
        } catch {
          return null;
        }
        const imageUrl = hardwareFirstCartImageUrl(
          product.id,
          variant,
          variant ? checkoutImageUrlForProductWithVariant(product.id, variant) : checkoutImageUrlForProduct(product.id),
        );
        const discount = hardwareDiscounts?.[cartItem.cartItemId];
        const isTrialItem = trialItemIds.includes(cartItem.cartItemId);
        // the line carries the code's share only; the multi-tracker saving is its own row below
        // a trial bundle's tracker is free today
        const giftPlan = giftedTrackerPlan?.trackerIds.has(cartItem.cartItemId) ? giftedTrackerPlan : undefined;
        const unitInCents = isTrialItem
          ? 0
          : discount
            ? discount.listPriceInCents - discount.promoDiscountInCents + (giftPlan?.planInCents ?? 0)
            : getLocalizedPricing(product.pricingInCents, locale.currency);
        // a tracker with no band is one size; an accessory simply has no band line
        const sizeLine = band ?? (discount ? t('hardware_first_checkout_one_size') : undefined);
        const variantLine = giftPlan
          ? [sizeLine, giftPlanRowLabel(t, giftPlan.months, 1)].filter(Boolean).join(' · ')
          : sizeLine;
        return (
          <li className={styles.item} key={cartItem.cartItemId}>
            <span className={styles.thumb}>
              <Image image={{ type: 'image', url: imageUrl }} alt="" />
            </span>
            <span className={styles.meta}>
              <span className={styles.name}>
                {name}
                {giftPlan && <span className={styles.giftTag}>{t('hardware_first_gift_tag')}</span>}
                {cartItem.quantity > 1 && (
                  <span className={styles.quantity}>
                    {t('hardware_first_checkout_quantity', { count: cartItem.quantity })}
                  </span>
                )}
              </span>
              {variantLine && <span className={styles.variant}>{variantLine}</span>}
            </span>
            <span className={styles.price}>{formatHardwarePrice(unitInCents * cartItem.quantity, locale)}</span>
          </li>
        );
      })}
    </ul>
  );
}
